import { v4 as uuid } from 'uuid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { PromoRequestItem } from '~/components/Promo/hooks/usePromoRequests';

type PromoLogFlatItem = definitions['Promo log flat item'];
type DiscountRequestItem = definitions['Discount Request Item'];
type DiscountRequestOrder = definitions['Discount Request']['order'];

function mapLogDeviceToOrderDevice(deviceType: string | null | undefined): DiscountRequestOrder['deviceType'] {
  const d = (deviceType ?? '').toLowerCase();
  if (d.includes('ios')) {
    return 'ios';
  }
  if (d.includes('android')) {
    return 'android';
  }
  return 'web';
}

function buildMinimalOrderFromFlatItem(flat: PromoLogFlatItem, item: DiscountRequestItem): DiscountRequestOrder {
  return {
    region: 'AU',
    brand: 'luxuryescapes',
    isGiftOrder: false,
    hasBedbankPromotion: false,
    deviceType: mapLogDeviceToOrderDevice(flat.deviceType),
    clientOrderVersion: flat.clientOrderVersion ?? undefined,
    items: [item],
  };
}

function flatItemToDiscountRequestItem(flat: PromoLogFlatItem): DiscountRequestItem {
  const categoryBK = (flat.categoryBk ?? 'hotel') as DiscountRequestItem['categoryBK'];
  const subCategoryBK = flat.subCategoryBk as DiscountRequestItem['subCategoryBK'] | undefined;

  return {
    offerId: flat.offerId ?? undefined,
    itemId: flat.itemId ?? undefined,
    mx: flat.mx ?? undefined,
    loyaltyOfferType: flat.loyaltyOfferType ?? undefined,
    cartItemId: flat.cartItemId ?? undefined,
    isMemberPrice: flat.isMemberPrice ?? undefined,
    luxPlusPrice: flat.luxPlusPrice ?? undefined,
    provider: flat.provider ?? undefined,
    numberOfNights: flat.numberOfNights ?? undefined,
    numberOfAdults: flat.numberOfAdults ?? undefined,
    numberOfChildren: flat.numberOfChildren ?? undefined,
    discountableTotal: flat.discountableTotal ?? 0,
    travellers: (flat.travellers ?? undefined) as DiscountRequestItem['travellers'],
    reservationType: flat.reservationType as DiscountRequestItem['reservationType'],
    itemTypeSpecificId: flat.itemTypeSpecificId ?? undefined,
    categoryBK,
    subCategoryBK,
    itemCountryCode: flat.itemCountryCode ?? flat.countryCode ?? undefined,
    originAirportCode: flat.originAirportCode ?? undefined,
    destinationAirportCode: flat.destinationAirportCode ?? undefined,
    fareType: flat.fareType ?? undefined,
    transactionKey: flat.transactionKey ?? undefined,
    startDate: flat.startDate ?? undefined,
    endDate: flat.endDate ?? undefined,
    itemInfoString: flat.itemInfoString ?? undefined,
    itemLat: flat.itemLat ?? undefined,
    itemLng: flat.itemLng ?? undefined,
    isFnFBooking: flat.fnfBooking ?? undefined,
  };
}

/**
 * Maps `getLogsV2` `log_items_2` (`Promo log flat item`) rows into the same `PromoRequestItem` shape used by Promo Request Items.
 */
export const parsePromoRequestItemsFromPromoRequest = (
  logItems2: Array<PromoLogFlatItem | null | undefined>,
): Array<PromoRequestItem> => {
  return (logItems2 ?? [])
    .filter((row): row is PromoLogFlatItem => row != null)
    .map((flat) => {
      const item = flatItemToDiscountRequestItem(flat);
      const fullRequest = buildMinimalOrderFromFlatItem(flat, item);

      return {
        ...item,
        uniqueKey: uuid(),
        requestAt: flat.requestAt,
        isDeleted: false,
        deviceType: flat.deviceType ?? undefined,
        codeName: flat.codeName,
        orderId: flat.fkOrders ?? undefined,
        clientOrderVersion: flat.clientOrderVersion ?? undefined,
        currencyCode: flat.currencyCode ?? flat.currency ?? undefined,
        userId: flat.user,
        fullRequest,
        discountItem: null,
        statusCode: null,
        errorCode: flat.metadata?.errorCode ?? undefined,
        errorMsg: flat.metadata?.errorMessage ?? flat.statusMessage ?? undefined,
        requestSource: undefined,
        vouchersApplied: flat.vouchersApplied,
        voucherTotal: flat.voucherTotal,
        statusMessage: flat.statusMessage,
        warningCode: flat.warningCode,
        countryCode: flat.countryCode ?? undefined,
        logLineDetails: flat.details,
        logMetadata: flat.metadata,
        linePrice: flat.price,
        logProductType: flat.productType,
      };
    });
};
